import { ENTER_NAME } from '@anm/constants/keyCodes';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { Password } from '@anm/hosting-player';
import { Config as FinalFormConfig } from 'final-form';
import { useCallback, FC, KeyboardEvent } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import { PasswordField, TextField } from '../fields';

import { ButtonWrapper, ControlGroup, FormWrapper, PasswordTip, SaveButton } from './Wrapper';

type HOCs = FinalFormConfig;

type PasswordFormProps = {
  onBlur: (value: Password) => void;
  wasChanged: boolean;
  showTip: boolean;
  isPending?: boolean;
};

const renderForm = ({ showTip = false, isPending = false, wasChanged, onBlur: handleBlur }: PasswordFormProps) => ({
  handleSubmit,
  values
}: FormRenderProps) => {
  const handleFormBlur = useCallback(
    () =>
      handleBlur({
        password: values.password || '',
        description: values.description || ''
      }),
    [values]
  );

  const handleKeyDown = useCallback(
    ({ code }: KeyboardEvent<HTMLInputElement>) => {
      if (code !== ENTER_NAME) return;
      handleBlur(values);
      setTimeout(() => handleSubmit(), 0);
    },
    [values]
  );

  return (
    <FormWrapper onBlur={handleFormBlur} onSubmit={handleSubmit}>
      <ControlGroup>
        <TextField
          name="description"
          label="Message"
          inputVariant="simple-small"
          labelVariant="static-above"
          onKeyDown={handleKeyDown}
        />
        <PasswordField
          isSimple
          name="password"
          label="Password"
          placeholder="Enter your password"
          inputVariant="simple-small"
          labelVariant="static-above"
          onKeyDown={handleKeyDown}
        />
      </ControlGroup>
      {showTip && (
        <PasswordTip>
          No password specified. Please enter the password your viewers need to know to access this video.
        </PasswordTip>
      )}

      {isMobileOrIPad() && (
        <ButtonWrapper>
          <SaveButton
            size="large_long"
            variant="light-blue"
            buttonType="submit"
            isPending={isPending}
            disabled={!wasChanged}
          >
            {wasChanged ? 'Save' : 'Saved'}
          </SaveButton>
        </ButtonWrapper>
      )}
    </FormWrapper>
  );
};

const PasswordForm: FC<HOCs & PasswordFormProps> = props => <Form render={renderForm(props)} {...props} />;

export default PasswordForm;
